import { logUsage } from 'owa-analytics';
import { getUserAttachmentPolicyChecker } from 'owa-attachment-policy';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    DATA_AUTH_ATTTIBUTE_NAME,
    lazyEvaluateSafeLink,
    lazyIsSafeLink,
    ORIGINALSRC_ATTTIBUTE_NAME,
    VERIFIED_SAFE_LINK,
} from 'owa-safelinks-evaluator';
import {
    lazyExtractFileUrlFromOfficeSharePointLink,
    lazyIsOfficeSharePointLink,
    lazyIsSharePointLink,
    lazyIsSharePointServiceGeneratedLink,
} from 'owa-sharepoint-link-detector';
import { orchestrator } from 'satcheljs';
import {
    addGenericLinkClickListener,
    addOpenDocumentPreviewEventListener,
} from '../actions/internalActions';
import { tryProcessSharePointLink } from '../actions/publicActions';
import shouldUseClickHandlerOnSharePointLink from '../utils/shouldUseClickHandlerOnSharePointLink';

/*
 * In this orchestrator, it will add click handler to anchor element
 * a) if it is a SharePoint service generated link (with URL query at=9)
 *    addGenericLinkClickListener will be called and
 *    the click handler will add telemetry information for the 'opening link fast' project, or
 * b) if it is a regular SharePoint link,
 *    addOpenDocumentPreviewEventListener will be called,
 *    the click handler will try to open the link in SxS.
 *    In the case of non-mail module where SxS is not available,
 *    there is nothing subscribing to the action addOpenDocumentPreviewEventListener
 *    so those links will work as any other link
 */

orchestrator(tryProcessSharePointLink, async actionMessage => {
    const { element, itemId, sxsId } = actionMessage;

    const policyChecker = getUserAttachmentPolicyChecker();
    const linkPreviewEnabledByPolicy = policyChecker.linkPreviewEnabled;

    if (element.tagName.toLowerCase() === 'a' && !isOwaReferenceAttachment(element)) {
        const anchorElement = <HTMLAnchorElement>element;
        const isSharePointLink = await lazyIsSharePointLink.import();
        const isSharePointServiceGeneratedLink = await lazyIsSharePointServiceGeneratedLink.import();
        const isSafeLink = await lazyIsSafeLink.import();
        const isOfficeSharePointLink = await lazyIsOfficeSharePointLink.import();
        const evaluateSafeLink: (
            safeLinkUrl: string
        ) => Promise<string> = await lazyEvaluateSafeLink.import();
        if (await isSafeLink(anchorElement)) {
            let href = element.getAttribute(ORIGINALSRC_ATTTIBUTE_NAME);
            const isSafeLinkVerified =
                element.getAttribute(DATA_AUTH_ATTTIBUTE_NAME) === VERIFIED_SAFE_LINK;
            const safeLinkUrl = anchorElement.href;
            const isSafeLinkWrapped = true;
            if (isSharePointLink(href)) {
                if (shouldUseClickHandlerOnSharePointLink(href)) {
                    let safeLinkEvaluateResult: string;
                    if (isSharePointServiceGeneratedLink(href)) {
                        addGenericLinkClickListener(element, isSafeLinkWrapped, isSafeLinkVerified);
                    } else if (
                        isFeatureEnabled('doc-SxS-jsApi-ODB-Links') &&
                        linkPreviewEnabledByPolicy &&
                        isSafeLinkVerified
                    ) {
                        addOpenDocumentPreviewEventListener(
                            element,
                            href /* Don't use safe link for JsAPI as it will be handled on SharePoint side */,
                            itemId,
                            isSafeLinkWrapped,
                            isSafeLinkVerified,
                            false,
                            sxsId
                        );
                    } else if (
                        isFeatureEnabled('doc-SxS-jsApi-ODB-Links') &&
                        linkPreviewEnabledByPolicy &&
                        !isSafeLinkVerified &&
                        !!(safeLinkEvaluateResult = await evaluateSafeLink(safeLinkUrl))
                    ) {
                        addOpenDocumentPreviewEventListener(
                            element,
                            safeLinkEvaluateResult /* Don't use safe link for JsAPI as it will be handled on SharePoint side */,
                            itemId,
                            isSafeLinkWrapped,
                            isSafeLinkVerified,
                            false,
                            sxsId
                        );
                    } else {
                        addGenericLinkClickListener(element, isSafeLinkWrapped, isSafeLinkVerified);
                    }
                }

                logSharePointLinkFound();
            } else if (isOfficeSharePointLink(href)) {
                if (!isSafeLinkVerified) {
                    href = await evaluateSafeLink(safeLinkUrl);
                }
                const extractFileUrlFromOfficeSharePointLink = await lazyExtractFileUrlFromOfficeSharePointLink.import();
                const sharePointOriginalUrl: string = extractFileUrlFromOfficeSharePointLink(href);
                if (!!sharePointOriginalUrl && linkPreviewEnabledByPolicy) {
                    addOpenDocumentPreviewEventListener(
                        element,
                        sharePointOriginalUrl /* Don't use safe link for JsAPI as it will be handled on SharePoint side */,
                        itemId,
                        isSafeLinkWrapped,
                        isSafeLinkVerified,
                        true,
                        sxsId
                    );
                }
            }
        } else {
            const href = anchorElement.href;
            const isSafeLinkWrapped = false;
            const isSafeLinkVerified = false;
            if (isSharePointLink(href)) {
                if (shouldUseClickHandlerOnSharePointLink(href)) {
                    if (isSharePointServiceGeneratedLink(href)) {
                        addGenericLinkClickListener(element, isSafeLinkWrapped, isSafeLinkVerified);
                    } else if (
                        isFeatureEnabled('doc-SxS-jsApi-ODB-Links') &&
                        linkPreviewEnabledByPolicy
                    ) {
                        addOpenDocumentPreviewEventListener(
                            element,
                            href,
                            itemId,
                            isSafeLinkWrapped,
                            isSafeLinkVerified,
                            false,
                            sxsId
                        );
                    } else {
                        addGenericLinkClickListener(element, isSafeLinkWrapped, isSafeLinkVerified);
                    }
                }

                logSharePointLinkFound();
            } else if (isOfficeSharePointLink(href)) {
                const extractFileUrlFromOfficeSharePointLink = await lazyExtractFileUrlFromOfficeSharePointLink.import();
                const sharePointOriginalUrl: string = extractFileUrlFromOfficeSharePointLink(href);
                if (!!sharePointOriginalUrl && linkPreviewEnabledByPolicy) {
                    addOpenDocumentPreviewEventListener(
                        element,
                        sharePointOriginalUrl /* Don't use safe link for JsAPI as it will be handled on SharePoint side */,
                        itemId,
                        isSafeLinkWrapped,
                        isSafeLinkVerified,
                        true,
                        sxsId
                    );
                }
            }
        }
    }
});

let sharePointLinkFound = false;
function logSharePointLinkFound() {
    // We are only interested for unique user count right now
    // Only log this datapoint once per session
    if (!sharePointLinkFound) {
        sharePointLinkFound = true;
        logUsage('SharePointLinkFound');
    }
}

function isOwaReferenceAttachment(element: HTMLElement) {
    if (element.id === 'OwaReferenceAttachments') {
        return true;
    }
    if (element.parentElement) {
        return isOwaReferenceAttachment(element.parentElement);
    } else {
        return false;
    }
}
